import {
  FileArrowUpIcon,
  InfoIcon,
  PlusCircleIcon,
} from '@phosphor-icons/react';
import { ChangeEvent, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { FileMimeTypes, PaginatedTable } from '@/modules/common';
import { FiltersChangeType } from '@/modules/events/hooks';
import { EventParticipant } from '@/modules/events/types';

import {
  CreateParticipantTypeDialog,
  ParticipantDetailsDialog,
  ParticipantsEventActions,
  ParticipantsTableFilters,
  SendSmsDialog,
} from './components';
import { useParticipantsTab } from './hooks/use-participants-tab';

export function ParticipantsTab() {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedRow, setSelectedRow] = useState<EventParticipant | null>(null);
  const [isSendSmsDialogOpen, setIsSendSmsDialogOpen] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    []
  );
  const [
    isCreateParticipantTypeDialogOpen,
    setIsCreateParticipantTypeDialogOpen,
  ] = useState(false);

  const {
    eventId,
    columns,
    participants,
    participantTypesOptions,
    participantTypes,
    isLoading,
    formattedParticipants,
    isGeneratingReport,
    tableProps,
    filters,
    setFilters,
    getGenerateReportUrl,
    pickFileAndUpload,
  } = useParticipantsTab();

  const selectedParticipantsIds = useMemo(() => {
    return participants
      .filter((participant) => selectedParticipants.includes(participant.id))
      .map((participant) => participant.id);
  }, [selectedParticipants]);

  const pickFile = () => {
    fileInputRef.current?.click();
  };

  const handleUploadCSV = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      await pickFileAndUpload(event);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <input
        id="csv-file"
        type="file"
        ref={fileInputRef}
        className="hidden"
        data-testid="upload-file-input"
        onChange={handleUploadCSV}
        accept={FileMimeTypes.Csv}
      />
      <p className="font-semibold text-lg">Participants Table</p>

      <div className="flex flex-col gap-4">
        <div className="py-5 flex gap-5 items-center justify-between bg-zinc-900 rounded-lg border border-zinc-800 p-4">
          <div className="flex gap-2 items-center">
            <InfoIcon />
            <div className="text-xs">
              <p className="font-semibold">Manage existing participant types</p>
              <p>Use the "Participant Types button" to view or update them.</p>
            </div>
          </div>

          <Button onClick={() => setIsCreateParticipantTypeDialogOpen(true)}>
            Participant Types
          </Button>
        </div>

        <PaginatedTable<EventParticipant>
          pinEnabled
          data={participants}
          columns={columns}
          searchable
          searchPlaceholder="Search by phone name or email"
          sizeStyleProperty="minWidth"
          isLoading={isLoading}
          tableCellClassName="px-2 py-1"
          tableRowClassName="hover:bg-transparent"
          onClick={setSelectedRow}
          totalCountLabel="participants"
          onRowSelectionChange={setSelectedParticipants}
          initialState={{
            columnPinning: {
              right: ['actions'],
              left: ['select', 'firstName', 'lastName', 'externalId'],
            },
          }}
          disabledColumnsIdsToClick={[
            'select',
            'type',
            'usagesLeft',
            'actions',
          ]}
          {...(participantTypes.length
            ? {
                emptyTitle: 'Upload participants from CSV file.',
                emptyDescription:
                  'The participants will be uploaded here. Please upload new ones.',
                emptyButton: (
                  <Button onClick={pickFile}>
                    <FileArrowUpIcon />
                    Upload CSV
                  </Button>
                ),
              }
            : {
                emptyTitle:
                  'Create Participant Types before uploading the Participant CSV file.',
                emptyDescription:
                  'You can create “Participant Types” only in published event.',
                emptyButton: (
                  <Button
                    onClick={() => setIsCreateParticipantTypeDialogOpen(true)}
                  >
                    <PlusCircleIcon />
                    Create Participant Types
                  </Button>
                ),
              })}
          {...tableProps}
        >
          <div className="flex gap-4 items-center justify-center w-full">
            <ParticipantsTableFilters
              types={participantTypesOptions}
              filters={filters}
              onFilters={setFilters as FiltersChangeType}
            />

            <div className="flex gap-2">
              <Button
                disabled={!participantTypes.length}
                variant="outline"
                onClick={pickFile}
                data-testid="upload-file-button"
              >
                <FileArrowUpIcon />
                Upload CSV
              </Button>

              {formattedParticipants.length > 0 && (
                <ParticipantsEventActions
                  eventId={eventId}
                  onGenerateReport={getGenerateReportUrl}
                  isGeneratingReport={isGeneratingReport}
                />
              )}
            </div>

            {Object.keys(selectedParticipants).length > 0 && (
              <Button onClick={() => setIsSendSmsDialogOpen(true)}>
                Send SMS
              </Button>
            )}
          </div>
        </PaginatedTable>
      </div>

      <CreateParticipantTypeDialog
        isOpen={isCreateParticipantTypeDialogOpen}
        eventId={eventId}
        participants={formattedParticipants}
        participantTypes={participantTypes}
        onOpenChange={setIsCreateParticipantTypeDialogOpen}
      />

      <SendSmsDialog
        eventId={eventId}
        isOpen={isSendSmsDialogOpen}
        onOpenChange={setIsSendSmsDialogOpen}
        participantIds={selectedParticipantsIds}
      />

      {selectedRow && (
        <ParticipantDetailsDialog
          isOpen={!!selectedRow}
          eventId={eventId}
          selectedParticipant={selectedRow}
          participantTypes={participantTypes}
          onOpenChange={() => setSelectedRow(null)}
          onSuccess={() => tableProps.refetch()}
        />
      )}
    </div>
  );
}
